import React, { useState } from 'react';
import {
  Clock,
  Target,
  AlertTriangle,
  Check,
  PenTool,
  Mail,
  UserCheck,
  Loader2,
  FileText
} from 'lucide-react';
import { Client, Provider, UserSession } from '../../types';

export interface SoapSectionData {
  dos: string;
  time_in: string;
  time_out: string;
  duration_minutes: number;
  service_code: string;
  clients_treated_together?: 1 | 2 | 3 | '' | null;
  clinician_evidence: string;
  session_narrative: string;
  targeted_goals: {
    [goalId: string]: {
      targeted: boolean;
      accuracy: number;
      cueing: string;
      evidenceNote?: string;
    };
  };
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  medical_necessity: string;
  guardian_name: string;
  guardian_email: string;
  guardian_relationship: string;
  attestation_checked: boolean;
}

export interface CanonicalGoal {
  id: string;
  description: string;
  baseline_performance?: string;
  target_percentage?: number;
  current_percentage?: number;
  status?: string;
  target_date?: string;
}

interface ManualSoapWorkspaceProps {
  data: SoapSectionData;
  onChange: (updated: SoapSectionData) => void;
  client: Client | null;
  canonicalGoals: CanonicalGoal[];
  provider: Provider | null;
  currentUser: UserSession | null;
  isUnder53Minutes: boolean;
  isManualSoapApproved: boolean;
  manualSoapReason?: string | null;
  manualSoapNote?: string | null;
  onFocusSection?: (sectionKey: string) => void;
}

const ACCURACY_PRESETS = [60, 70, 75, 80, 85, 90, 100];
const CUEING_OPTIONS = [
  'Min Verbal',
  'Mod Verbal',
  'Max Verbal',
  'Min Visual',
  'Mod Visual',
  'Tactile Prompt',
  'Independent'
];

export const ManualSoapWorkspace: React.FC<ManualSoapWorkspaceProps> = ({
  data,
  onChange,
  client,
  canonicalGoals = [],
  provider,
  currentUser,
  isUnder53Minutes,
  isManualSoapApproved,
  manualSoapReason,
  manualSoapNote,
  onFocusSection
}) => {
  const [isDrafting, setIsDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);

  const updateField = <K extends keyof SoapSectionData>(field: K, value: SoapSectionData[K]) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  const durationFromClock = (timeIn: string, timeOut: string, fallback: number): number => {
    if (!timeIn || !timeOut) return fallback;
    const [hIn, mIn] = timeIn.split(':').map(Number);
    const [hOut, mOut] = timeOut.split(':').map(Number);
    if ([hIn, mIn, hOut, mOut].some((n) => Number.isNaN(n))) return fallback;
    let diff = hOut * 60 + mOut - (hIn * 60 + mIn);
    if (diff < 0) diff += 24 * 60;
    return diff;
  };

  // Toggle goal targeted state
  const handleToggleGoal = (goalId: string) => {
    const current = data.targeted_goals[goalId] || {
      targeted: false,
      accuracy: 75,
      cueing: 'Mod Visual',
      evidenceNote: ''
    };

    const nextTargeted = !current.targeted;
    updateField('targeted_goals', {
      ...data.targeted_goals,
      [goalId]: {
        ...current,
        targeted: nextTargeted
      }
    });
  };

  const handleSetAccuracy = (goalId: string, accuracy: number) => {
    const clamped = Math.min(100, Math.max(0, accuracy));
    const current = data.targeted_goals[goalId] || {
      targeted: true,
      accuracy: 75,
      cueing: 'Mod Visual',
      evidenceNote: ''
    };
    updateField('targeted_goals', {
      ...data.targeted_goals,
      [goalId]: { ...current, accuracy: clamped }
    });
  };

  const handleSetCueing = (goalId: string, cueing: string) => {
    const current = data.targeted_goals[goalId] || {
      targeted: true,
      accuracy: 75,
      cueing: 'Mod Visual',
      evidenceNote: ''
    };
    updateField('targeted_goals', {
      ...data.targeted_goals,
      [goalId]: { ...current, cueing }
    });
  };

  const handleSetEvidence = (goalId: string, evidenceNote: string) => {
    const current = data.targeted_goals[goalId] || {
      targeted: true,
      accuracy: 75,
      cueing: 'Mod Visual',
      evidenceNote: ''
    };
    updateField('targeted_goals', {
      ...data.targeted_goals,
      [goalId]: { ...current, evidenceNote }
    });
  };

  // Draft with Pulse AI Assist
  const handleDraftSoap = async () => {
    if (!client) return;
    setIsDrafting(true);
    setDraftError(null);

    const targetedGoalsList = canonicalGoals
      .filter((g) => data.targeted_goals[g.id]?.targeted)
      .map((g) => ({
        id: g.id,
        description: g.description,
        targetPercentage: g.target_percentage || 80,
        accuracy: data.targeted_goals[g.id]?.accuracy ?? 75,
        cueing: data.targeted_goals[g.id]?.cueing || 'Mod Visual',
        evidenceNote: data.targeted_goals[g.id]?.evidenceNote || ''
      }));

    try {
      const res = await fetch('/api/clinical/docs/draft-soap', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientName: `${client.first_name} ${client.last_name}`,
          dos: data.dos,
          durationMinutes: data.duration_minutes,
          startTime: data.time_in,
          endTime: data.time_out,
          serviceCode: data.service_code || '92507',
          targetedGoals: targetedGoalsList,
          clinicianEvidence: data.clinician_evidence,
          sessionNarrative: data.session_narrative,
          additionalObjectiveNotes: data.objective
        })
      });

      if (!res.ok) {
        throw new Error('Failed to generate draft from session evidence');
      }

      const draft = await res.json();
      onChange({
        ...data,
        subjective: draft.subjective || data.subjective,
        objective: draft.objective || data.objective,
        assessment: draft.assessment || data.assessment,
        plan: draft.plan || data.plan,
        medical_necessity: draft.medical_necessity || data.medical_necessity
      });
    } catch (err: any) {
      console.error('Error drafting SOAP:', err);
      setDraftError(err.message || 'Draft generation failed. You can type directly below.');
    } finally {
      setIsDrafting(false);
    }
  };

  const targetedCount = canonicalGoals.filter((g) => data.targeted_goals[g.id]?.targeted).length;

  // Treating clinician credential resolution
  const treatingClinicianName = provider?.name || currentUser?.name || 'Treating Clinician';
  const treatingRole = provider?.role || (currentUser?.role === 'ASSISTANT' ? 'SLPA' : 'SLP');
  const isSlpaAuthor = treatingRole === 'SLPA' || currentUser?.role === 'ASSISTANT';

  return (
    <div className="space-y-5">
      {/* 1. Live Encounter Instrument Bezel (Matching Mobile SOAP Precision) */}
      <div className="relative bg-[#f8fafc] border border-slate-200 rounded-2xl p-5 text-center shadow-[0_1px_3px_rgba(15,23,42,0.06)] space-y-4">
        <div className="relative flex flex-wrap items-center justify-between text-[11px] font-bold text-slate-500 uppercase tracking-wider px-1 gap-2">
          <div className="flex items-center space-x-2 text-cyan-700 font-mono">
            <span className="w-2 h-2 rounded-full bg-cyan-600" />
            <span>
              MANUAL ENCOUNTER (DESKTOP)
            </span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
              SignWell Remote Workflow
            </span>
          </div>
          <div className="font-mono text-slate-500 font-normal">
            CPT {data.service_code || '92507'} • <span className="font-semibold text-slate-800">{data.dos}</span>
          </div>
        </div>

        {/* Timestamp Inputs Grid */}
        <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-3 text-left">
          <div>
            <label className="block text-[10px] font-semibold text-slate-600 mb-1">Date of Service (DOS)</label>
            <input
              type="date"
              value={data.dos}
              onChange={(e) => updateField('dos', e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 font-mono focus:bg-white focus:border-slate-400 focus:ring-1 focus:ring-slate-400/40 focus:outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-600 mb-1">Time In</label>
            <input
              type="time"
              value={data.time_in}
              onChange={(e) => {
                const newIn = e.target.value;
                onChange({
                  ...data,
                  time_in: newIn,
                  duration_minutes: durationFromClock(newIn, data.time_out, data.duration_minutes)
                });
              }}
              className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 font-mono focus:bg-white focus:border-slate-400 focus:ring-1 focus:ring-slate-400/40 focus:outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-600 mb-1">Time Out</label>
            <input
              type="time"
              value={data.time_out}
              onChange={(e) => {
                const newOut = e.target.value;
                onChange({
                  ...data,
                  time_out: newOut,
                  duration_minutes: durationFromClock(data.time_in, newOut, data.duration_minutes)
                });
              }}
              className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 font-mono focus:bg-white focus:border-slate-400 focus:ring-1 focus:ring-slate-400/40 focus:outline-none"
              required
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-600 mb-1">Duration</label>
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                readOnly
                tabIndex={-1}
                value={data.duration_minutes}
                aria-readonly="true"
                className="w-full bg-slate-100 border border-slate-200 rounded-lg px-2.5 py-1.5 text-sm text-slate-800 font-mono font-semibold focus:outline-none"
              />
              <span className="text-sm font-semibold font-mono text-slate-600 shrink-0">min</span>
            </div>
          </div>
          <div className="col-span-2 sm:col-span-4">
            <label className="block text-[10px] font-semibold text-slate-600 mb-1">Clients treated together</label>
            <select
              value={data.clients_treated_together === 1 || data.clients_treated_together === 2 || data.clients_treated_together === 3 ? String(data.clients_treated_together) : ''}
              onChange={(e) => {
                const next = e.target.value;
                onChange({
                  ...data,
                  clients_treated_together: next === '1' || next === '2' || next === '3' ? Number(next) as 1 | 2 | 3 : ''
                });
              }}
              className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 focus:bg-white focus:border-slate-400 focus:ring-1 focus:ring-slate-400/40 focus:outline-none"
              required
            >
              <option value="">Select individual or group…</option>
              <option value="1">Individual treatment (1 client)</option>
              <option value="2">Group treatment (2 clients)</option>
              <option value="3">Group treatment (3 clients)</option>
            </select>
          </div>
        </div>

        {/* DDD 53-Minute Notice Badge */}
        {isUnder53Minutes ? (
          <div className="relative flex items-center justify-center space-x-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3.5 py-2 rounded-xl text-center">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600" />
            <span className="font-medium">Notice: Session duration ({data.duration_minutes} min) is under 53 minutes. Under DDD rules, note completion is permitted.</span>
          </div>
        ) : (
          <div className="relative flex items-center justify-center space-x-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 px-3.5 py-1.5 rounded-xl text-center">
            <Check className="w-4 h-4 flex-shrink-0 text-emerald-600" />
            <span className="font-medium">Minimum session duration met (53+ minutes).</span>
          </div>
        )}
      </div>

      {/* 2. Quick Clinician Observation / Evidence */}
      <div className="bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 rounded-2xl p-5 space-y-2.5 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)]">
        <label className="block text-xs font-bold text-slate-200 uppercase tracking-wider">
          Point-of-Care Notes / Evidence
        </label>
        <textarea
          rows={2}
          value={data.clinician_evidence}
          onChange={(e) => updateField('clinician_evidence', e.target.value)}
          className="w-full bg-[#0a101b] border border-slate-700/80 rounded-xl p-3 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:bg-[#0c1424] focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 shadow-inner"
        />
      </div>

      {/* 3. Primary Clinician Writing Field: Session Narrative (Required) */}
      <div className="bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 rounded-2xl p-5 space-y-3 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <label className="block text-xs font-bold text-slate-200 uppercase tracking-wider">
              Session Narrative
            </label>
            <span className="text-[10px] font-semibold text-slate-700 bg-slate-50 border border-slate-300 px-1.5 py-0.5 rounded uppercase tracking-wider">
              Required
            </span>
          </div>
          <span className="text-[10px] text-slate-400 font-mono">Primary Documentation</span>
        </div>

        <textarea
          rows={4}
          value={data.session_narrative}
          onChange={(e) => updateField('session_narrative', e.target.value)}
          className="w-full bg-[#0a101b] border border-slate-700/80 focus:border-cyan-500 rounded-xl p-3.5 text-xs sm:text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:bg-[#0c1424] focus:ring-1 focus:ring-cyan-500/30 leading-relaxed font-sans min-h-[110px] shadow-inner"
        />
      </div>

      {/* 4. Objective Data — Goals & Performance Section */}
      <div className="space-y-3 p-4 sm:p-5 rounded-2xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)]">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white flex items-center space-x-2">
            <Target className="w-4 h-4 text-sky-400" />
            <span>Objective Data — Goals & Performance ({canonicalGoals.length})</span>
          </h3>
          <span className="text-xs text-slate-400 font-mono">
            {targetedCount} targeted today
          </span>
        </div>

        {canonicalGoals.length === 0 ? (
          <div className="bg-[#0a101b] border border-slate-800 rounded-xl p-4 text-center text-xs text-slate-400 shadow-inner">
            No active goals established yet in client chart. You can document objective observations directly below.
          </div>
        ) : (
          <div className="space-y-3">
            {canonicalGoals.map((goal, idx) => {
              const prog = data.targeted_goals[goal.id] || {
                targeted: false,
                accuracy: goal.target_percentage || 80,
                cueing: 'Mod Visual',
                evidenceNote: ''
              };

              return (
                <div
                  key={goal.id}
                  className={`border rounded-2xl p-4 transition-all ${
                    prog.targeted
                      ? 'bg-[#121c2e] border-sky-500/60 shadow-md shadow-sky-500/5'
                      : 'bg-[#101726] border-slate-700/70'
                  }`}
                >
                  {/* Goal Header & Toggle */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-slate-50 text-slate-700 border border-slate-300">
                          Goal #{idx + 1}
                        </span>
                        {goal.target_percentage && (
                          <span className="text-[10px] font-mono text-slate-400">
                            Target: {goal.target_percentage}%
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-200 leading-relaxed font-medium">
                        {goal.description}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleToggleGoal(goal.id)}
                      className={`min-h-[40px] px-3.5 py-2 rounded-xl text-xs font-bold flex items-center space-x-1.5 transition-all duration-150 cursor-pointer ${
                        prog.targeted
                          ? 'bg-sky-600 text-white shadow-md shadow-sky-600/30'
                          : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-300 shadow-xs'
                      }`}
                    >
                      {prog.targeted ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Targeted</span>
                        </>
                      ) : (
                        <span>Target Today</span>
                      )}
                    </button>
                  </div>

                  {/* Targeted Controls */}
                  {prog.targeted && (
                    <div className="mt-4 pt-3 border-t border-slate-700/70 space-y-3">
                      {/* Accuracy Stepper & Presets */}
                      <div>
                        <div className="flex items-center justify-between text-xs font-semibold text-slate-300 mb-1.5">
                          <span>Accuracy / Performance:</span>
                          <span className="font-mono text-sm font-bold text-sky-400">
                            {prog.accuracy}%
                          </span>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => handleSetAccuracy(goal.id, prog.accuracy - 5)}
                            className="w-10 h-10 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold text-sm flex items-center justify-center border border-slate-700 active:scale-95 cursor-pointer"
                          >
                            -5%
                          </button>
                          <div className="flex-1 flex gap-1 overflow-x-auto py-1">
                            {ACCURACY_PRESETS.map((p) => (
                              <button
                                key={p}
                                type="button"
                                onClick={() => handleSetAccuracy(goal.id, p)}
                                className={`flex-1 min-h-[40px] py-1.5 px-1 rounded-xl text-xs font-bold font-mono transition-all cursor-pointer ${
                                  prog.accuracy === p
                                    ? 'bg-sky-600 text-white shadow-md'
                                    : 'bg-slate-800/80 text-slate-300 hover:bg-slate-800 border border-slate-700/60'
                                }`}
                              >
                                {p}%
                              </button>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleSetAccuracy(goal.id, prog.accuracy + 5)}
                            className="w-10 h-10 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-bold text-sm flex items-center justify-center border border-slate-700 active:scale-95 cursor-pointer"
                          >
                            +5%
                          </button>
                        </div>
                      </div>

                      {/* Cueing Level Chips */}
                      <div>
                        <span className="block text-xs font-semibold text-slate-300 mb-1.5">
                          Support / Cueing Level:
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {CUEING_OPTIONS.map((cue) => (
                            <button
                              key={cue}
                              type="button"
                              onClick={() => handleSetCueing(goal.id, cue)}
                              className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                                prog.cueing === cue
                                  ? 'bg-sky-500/20 text-sky-300 border border-sky-500/40 font-bold'
                                  : 'bg-slate-800 text-slate-400 hover:text-slate-200 border border-slate-700/60'
                              }`}
                            >
                              {cue}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Quick Evidence Note */}
                      <div>
                        <input
                          type="text"
                          value={prog.evidenceNote || ''}
                          onChange={(e) => handleSetEvidence(goal.id, e.target.value)}
                          className="w-full bg-[#0a101b] border border-slate-700/80 rounded-xl px-3 py-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:bg-[#0c1424] focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 shadow-inner"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Additional Objective Notes (Optional) */}
        <div className="space-y-2 pt-2 border-t border-slate-700/70">
          <div className="flex items-center justify-between">
            <label className="block text-xs font-semibold text-slate-300">
              Additional Objective Notes <span className="text-slate-500 font-normal">(Optional)</span>
            </label>
            <span className="text-[10px] text-slate-500 font-mono">Structured goal data above is primary</span>
          </div>
          <textarea
            rows={2}
            value={data.objective}
            onChange={(e) => updateField('objective', e.target.value)}
            onFocus={() => onFocusSection?.('objective')}
            className="w-full bg-[#0a101b] border border-slate-700/80 focus:border-cyan-500 rounded-xl p-3 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:bg-[#0c1424] focus:ring-1 focus:ring-cyan-500/30 leading-relaxed font-sans shadow-inner"
          />
        </div>
      </div>

      {/* 5. AI Assist Action */}
      <div className="flex items-center justify-center">
        <button
          type="button"
          onClick={handleDraftSoap}
          disabled={isDrafting}
          className="px-4 py-2 rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 text-white text-xs font-bold flex items-center space-x-2 shadow-md shadow-sky-900/30 transition-all cursor-pointer border border-sky-400/30 active:scale-[0.98] disabled:opacity-50 shrink-0"
        >
          {isDrafting ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
              <span>Drafting...</span>
            </>
          ) : (
            <>
              <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-5 h-5 shrink-0 object-contain" />
              <span>Draft from session</span>
            </>
          )}
        </button>
      </div>

      {draftError && (
        <div className="text-xs text-amber-900 bg-amber-50 border border-amber-300 p-2.5 rounded-lg text-center">
          {draftError}
        </div>
      )}

      {/* 6. Guardian / Legal Representative Section — Integrated SignWell Remote Workflow (NO ONSITE PAD) */}
      <div className="relative overflow-hidden rounded-2xl p-5 border border-indigo-500/40 bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-4">
        {/* Top specular highlight */}
        <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-indigo-300/40 to-transparent pointer-events-none" />

        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-indigo-500/30 pb-3">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-500/40 flex items-center justify-center text-indigo-300">
              <PenTool className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-xs font-bold text-white uppercase tracking-wider">
                Guardian / Legal Representative Remote Signature
              </h3>
              <p className="text-[11px] text-slate-300">
                Manual SOAP Exception Lane — SignWell electronic signature integration
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/40">
              SignWell e-Signature
            </span>
          </div>
        </div>

        {/* Notice explaining SignWell dispatch */}
        <div className="p-3 rounded-xl bg-[#121c2e] border border-indigo-500/30 text-indigo-200 text-xs space-y-1">
          <div className="flex items-center space-x-2 font-semibold text-indigo-300">
            <Mail className="w-4 h-4 text-indigo-400" />
            <span>Remote e-Signature Dispatch Standard</span>
          </div>
          <p className="text-[11px] text-indigo-200/80 leading-relaxed">
            Unlike Point-of-Care Mobile SOAP which captures an in-person signature, Manual SOAP exception notes are sent to the client's verified parent/guardian with Send to Guardian for eSign. Acknowledgment is due 24 hours after the session end in Arizona time.
          </p>
        </div>

        {/* Guardian Contact Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-slate-300 mb-1">
              Signer Name *
            </label>
            <input
              type="text"
              value={data.guardian_name}
              onChange={(e) => updateField('guardian_name', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 focus:bg-[#0c1424] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 focus:outline-none shadow-inner"
              required
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-300 mb-1">
              Signer Email Address (SignWell Recipient) *
            </label>
            <input
              type="email"
              value={data.guardian_email}
              onChange={(e) => updateField('guardian_email', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 focus:bg-[#0c1424] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 focus:outline-none font-mono shadow-inner"
              required
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-300 mb-1">
              Relationship to Client
            </label>
            <select
              value={data.guardian_relationship || 'Parent / Legal Guardian'}
              onChange={(e) => updateField('guardian_relationship', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg px-2.5 py-1.5 text-xs text-slate-100 focus:bg-[#0c1424] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 focus:outline-none shadow-inner"
            >
              <option value="Parent / Legal Guardian">Parent / Legal Guardian</option>
              <option value="Mother">Mother</option>
              <option value="Father">Father</option>
              <option value="Foster Parent / Case Worker">Foster Parent / Case Worker</option>
              <option value="Legal Representative / POA">Legal Representative / POA</option>
              <option value="Client / Self">Client / Self</option>
            </select>
          </div>
        </div>
      </div>

      {/* 8. Treating Clinician Attestation & Scope */}
      <div className="rounded-2xl p-5 border border-slate-700/80 bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] space-y-4 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)]">
        <div className="flex flex-wrap items-center justify-between gap-2 pb-3 border-b border-slate-700/70">
          <div className="flex items-center space-x-2">
            <UserCheck className="w-4 h-4 text-sky-400" />
            <span className="text-xs font-bold text-white uppercase tracking-wider">
              Treating Clinician Attestation
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-slate-300 font-semibold">{treatingClinicianName}</span>
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded font-semibold border ${
              isSlpaAuthor
                ? 'bg-amber-50 text-amber-900 border-amber-300'
                : 'bg-slate-50 text-slate-700 border-slate-300'
            }`}>
              {isSlpaAuthor ? 'SLPA (Requires Supervisor Co-sign)' : 'SLP (Direct Sign)'}
            </span>
          </div>
        </div>

        {isSlpaAuthor && (
          <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-300 p-2.5 rounded-xl leading-relaxed">
            Note for SLPAs: Upon document creation and guardian signature completion, this record will automatically route to your designated supervising SLP for clinical co-signature.
          </p>
        )}

        <label className="flex items-start space-x-3 cursor-pointer select-none p-3 rounded-xl bg-[#121c2e] border border-slate-700/70 hover:border-slate-600 transition-colors">
          <input
            type="checkbox"
            checked={data.attestation_checked}
            onChange={(e) => updateField('attestation_checked', e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded text-sky-500 focus:ring-0 border-slate-700 bg-slate-950 cursor-pointer"
          />
          <div className="text-xs text-slate-200 leading-relaxed font-medium">
            I attest that this note accurately reflects skilled clinical services personally rendered by <strong className="text-white">{treatingClinicianName}</strong> on <strong className="text-white">{data.dos}</strong> per the established plan of care.
          </div>
        </label>
      </div>
    </div>
  );
};
